import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { DateInput } from 'src/design/atoms/DateInput';
import { useFormik } from 'formik';
import React from 'react';
import { WbsRequestDto } from 'src/api/FiretrolApiClient';
import { SaveButton } from 'src/design/atoms/SaveButton';
import * as Yup from 'yup';
import { useWbs } from 'src/routes/Jobs/WBS/contexts/WbsContext';

const validationSchema = Yup.object().shape({
  bookedDate: Yup.date().nullable(),
  projectFinalDate: Yup.date().nullable(),
  designStartDate: Yup.date().nullable(),
  submittalDueDate: Yup.date().nullable(),
  materialOnJobDate: Yup.date().nullable(),
  installStartDate: Yup.date().nullable(),
  installCompleteDate: Yup.date().nullable(),
});

const initialValues: WbsRequestDto = {
  bookedDate: undefined,
  designStartDate: undefined,
  submittalDueDate: undefined,
  materialOnJobDate: undefined,
  installStartDate: undefined,
  installCompleteDate: undefined,
  projectFinalDate: undefined,
};

export const EditWbsForm: React.FC = () => {
  const { wbsData, loading, saving, saveWBS } = useWbs();
  const [error, setError] = React.useState<string | null>(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError(null);
      try {
        await saveWBS(values);
      } catch (err: any) {
        setError(err.message || 'Failed to save date settings');
      }
    },
  });

  React.useEffect(() => {
    if (wbsData) {
      formik.setValues({
        bookedDate: wbsData.bookedDate
          ? new Date(wbsData.bookedDate)
          : undefined,
        projectFinalDate: wbsData.projectFinalDate
          ? new Date(wbsData.projectFinalDate)
          : undefined,
        designStartDate: wbsData.designStartDate
          ? new Date(wbsData.designStartDate)
          : undefined,
        submittalDueDate: wbsData.submittalDueDate
          ? new Date(wbsData.submittalDueDate)
          : undefined,
        materialOnJobDate: wbsData.materialOnJobDate
          ? new Date(wbsData.materialOnJobDate)
          : undefined,
        installStartDate: wbsData.installStartDate
          ? new Date(wbsData.installStartDate)
          : undefined,
        installCompleteDate: wbsData.installCompleteDate
          ? new Date(wbsData.installCompleteDate)
          : undefined,
      });
    } else {
      formik.setValues(initialValues);
    }
  }, [wbsData]);

  return (
    <Paper>
      <Title order={4} mb="md">
        Job Date Settings
      </Title>

      {loading ? (
        <Stack align="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed" ta="center">
            Loading date settings...
          </Text>
        </Stack>
      ) : (
        <Stack gap="lg">
          <Title order={5}>Start and Finish Dates</Title>
          <SimpleGrid cols={2}>
            <DateInput
              label="Booked Date"
              placeholder="Select booked date"
              value={formik.values.bookedDate}
              onChange={(value) => formik.setFieldValue('bookedDate', value)}
              onBlur={formik.handleBlur}
              error={
                formik.touched.bookedDate && formik.errors.bookedDate
                  ? String(formik.errors.bookedDate)
                  : null
              }
              clearable
              disabled={loading}
            />
            <DateInput
              label="Project Final Date"
              placeholder="Select project final date"
              value={formik.values.projectFinalDate}
              onChange={(value) =>
                formik.setFieldValue('projectFinalDate', value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.projectFinalDate &&
                formik.errors.projectFinalDate
                  ? String(formik.errors.projectFinalDate)
                  : null
              }
              clearable
              disabled={loading}
            />
          </SimpleGrid>

          <Title order={5}>Engineering (10-50-xx)</Title>
          <SimpleGrid cols={2}>
            <DateInput
              label="Design Start Date"
              placeholder="Select design start date"
              value={formik.values.designStartDate}
              onChange={(value) =>
                formik.setFieldValue('designStartDate', value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.designStartDate && formik.errors.designStartDate
                  ? String(formik.errors.designStartDate)
                  : null
              }
              clearable
              disabled={loading}
            />
            <DateInput
              label="Submittal Due Date"
              placeholder="Select submittal due date"
              value={formik.values.submittalDueDate}
              onChange={(value) =>
                formik.setFieldValue('submittalDueDate', value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.submittalDueDate &&
                formik.errors.submittalDueDate
                  ? String(formik.errors.submittalDueDate)
                  : null
              }
              clearable
              disabled={loading}
            />
          </SimpleGrid>

          <Title order={5}>Material (20-xx-xx)</Title>
          <SimpleGrid cols={2}>
            <DateInput
              label="Material on Job Date"
              placeholder="Select material on job date"
              value={formik.values.materialOnJobDate}
              onChange={(value) =>
                formik.setFieldValue('materialOnJobDate', value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.materialOnJobDate &&
                formik.errors.materialOnJobDate
                  ? String(formik.errors.materialOnJobDate)
                  : null
              }
              clearable
              disabled={loading}
            />
            <Box />
          </SimpleGrid>

          <Title order={5}>Labor (10-40-xx)</Title>
          <SimpleGrid cols={2}>
            <DateInput
              label="Install Start Date"
              placeholder="Select install start date"
              value={formik.values.installStartDate}
              onChange={(value) =>
                formik.setFieldValue('installStartDate', value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.installStartDate &&
                formik.errors.installStartDate
                  ? String(formik.errors.installStartDate)
                  : null
              }
              clearable
              disabled={loading}
            />
            <DateInput
              label="Install Complete Date"
              placeholder="Select install complete date"
              value={formik.values.installCompleteDate}
              onChange={(value) =>
                formik.setFieldValue('installCompleteDate', value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.installCompleteDate &&
                formik.errors.installCompleteDate
                  ? String(formik.errors.installCompleteDate)
                  : null
              }
              clearable
              disabled={loading}
            />
          </SimpleGrid>

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          <Group justify="flex-end" mt="md">
            <SaveButton
              onClick={() => formik.handleSubmit()}
              disabled={!formik.isValid || !formik.dirty || loading}
              loading={formik.isSubmitting || saving}
            />
          </Group>
        </Stack>
      )}
    </Paper>
  );
};
